import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { styled } from "styled-components";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import { MenuItems } from "../contexts";
import { createAuctionPlaceholderImage } from "../lib/auctionPresentation";

const ArticleContainer = styled(Link)`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 22px;
  align-items: stretch;
  width: 100%;
  padding: 18px;
  border-radius: 24px;
  background:
    linear-gradient(180deg, rgba(246, 163, 28, 0.05) 0%, rgba(246, 163, 28, 0) 100%),
    ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  text-decoration: none;
  color: inherit;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 32px rgba(13, 18, 45, 0.08);
    border-color: ${({ theme }) => `${theme.colors.primary}55`};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 4px;
  }

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  @media (max-width: 767px) {
    padding: 14px;
    border-radius: 20px;
  }
`;

const ArticleImageContainer = styled.div`
  width: 100%;
  min-height: 220px;
  border-radius: 20px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 1023px) {
    min-height: 260px;
  }

  @media (max-width: 767px) {
    min-height: 220px;
  }
`;

const ArticleContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 18px;
  min-width: 0;
`;

const ArticleMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ArticleNumber = styled.div`
  width: fit-content;
  padding: 7px 12px;
  border-radius: 999px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.blue500};
  background: ${({ theme }) => `${theme.colors.primary}20`};
`;

const ArticleTitle = styled.h3`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 700;
  font-size: 32px;
  line-height: 1.14;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const ArticleExcerpt = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.blue500};
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 767px) {
    font-size: 16px;
    line-height: 1.6;
  }
`;

const ReadMoreRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: "Sofia Pro", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.primary};

  ${ArticleContainer}:hover & {
    text-decoration: underline;
  }
`;

interface BlogArticleCardProps {
  id: number;
  index?: number;
  title: string;
  excerpt: string;
  imageUrl?: string;
}

const BlogArticleCard = ({
  id,
  index,
  title,
  excerpt,
  imageUrl,
}: BlogArticleCardProps) => {
  const fallbackImage = createAuctionPlaceholderImage(title, "Blog");
  const [imageSrc, setImageSrc] = useState(imageUrl || fallbackImage);

  useEffect(() => {
    setImageSrc(imageUrl || fallbackImage);
  }, [fallbackImage, imageUrl]);

  return (
    <ArticleContainer to={`/${MenuItems.Blog}/${id}`}>
      <ArticleImageContainer>
        <img
          src={imageSrc}
          alt={title}
          onError={() => {
            if (imageSrc !== fallbackImage) {
              setImageSrc(fallbackImage);
            }
          }}
        />
      </ArticleImageContainer>

      <ArticleContent>
        <ArticleMeta>
          <ArticleNumber>{`Článek ${index ?? id}`}</ArticleNumber>
          <ArticleTitle>{title}</ArticleTitle>
          <ArticleExcerpt>{excerpt}</ArticleExcerpt>
        </ArticleMeta>

        <ReadMoreRow>
          Více informací
          <ArrowUpRightIcon style={{ width: 18, height: 18 }} />
        </ReadMoreRow>
      </ArticleContent>
    </ArticleContainer>
  );
};

export default BlogArticleCard;
